import React from 'react';
import { useDispatch } from 'react-redux';
import { type Moment } from 'moment';
import { FormattedDate, FormattedMessage } from 'react-intl';
import {
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography
} from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { Icon, showConfirmCancelDialog, useSettings, Utils } from 'app/main/components';

interface AtokaReportsDownloadDialogProps {
	/** The Company uid */
	compUid: string;
	/** The Company  Atoka id */
	atokaId: string;
	/** Function used to refresh the page */
	updateRefresh: VoidFunction;
}

export const showAtokaReportsDownloadDialog = (props: AtokaReportsDownloadDialogProps) =>
	openDialog({
		children: <AtokaReportsDownloadDialog {...props} />,
		maxWidth: 'xs',
		fullWidth: true
	});

/**  */
export default function AtokaReportsDownloadDialog(props: AtokaReportsDownloadDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [lastImportDate, setLastImportDate] = React.useState<Moment | undefined>();

	function showUpdateCompanyDialog({ forceRefresh }) {
		dispatch(
			showConfirmCancelDialog({
				title: t('ATOKA_DIALOG_UPDATE_COMPANY_TITLE'),
				content: (
					<ul className="list-disc pl-24">
						<li>{t('DETAILS_COMPANIES_FIELD_TITLE')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_TAXCODE')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS_CITY')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS_ZIP')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS_DISTRICT')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS_REGION')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ADDRESS_COUNTRY')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_PEC')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ATECO')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_EMPLOYEES')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_REVENUE')}</li>
						<li>{t('DETAILS_COMPANIES_FIELD_ESTABLISHMENT_DATE')}</li>
					</ul>
				),
				onCancel: () => downloadFinancialStatements({ forceRefresh, updateCompany: false }),
				onConfirm: () => downloadFinancialStatements({ forceRefresh, updateCompany: true }),
				dialogProps: { maxWidth: 'xs', fullWidth: true }
			})
		);
	}

	function downloadFinancialStatements({ forceRefresh, updateCompany }) {
		dispatch(Utils.alertProgressMsg(t));
		Utils.executeApi.post({
			url: `/api/atoka/companies/${props.compUid}?updateCompany=${updateCompany}&forceRefresh=${forceRefresh}`,
			dispatch,
			successHandler: () => {
				dispatch(Utils.alertSuccessMsg(t));
				dispatch(closeDialog());
				props.updateRefresh();
			}
		});
	}

	React.useEffect(() => {
		Utils.executeStored({
			action: 'ext_st_atoka_financialstatements_check_latest',
			params: { comp_atoka_id: props.atokaId },
			dispatch,
			successHandler: response => setLastImportDate(Utils.parseDate(response.data.last_d_import))
		});
	}, [dispatch, props.atokaId]);

	return (
		<>
			<DialogTitle>{t('ATOKA_DIALOG_DOWNLOAD_TITLE')}</DialogTitle>
			<DialogContent className="pb-0">
				<Typography className="mb-8">{t('ATOKA_DIALOG_DOWNLOAD_DESCRIPTION')}</Typography>
				<List dense disablePadding>
					<ListItem disablePadding>
						<ListItemButton onClick={() => showUpdateCompanyDialog({ forceRefresh: true })}>
							<ListItemIcon>
								<Icon icon="cloud-download" size={32} />
							</ListItemIcon>
							<ListItemText
								primary={t('ATOKA_DIALOG_DOWNLOAD_OPTION_ATOKA')}
								secondary={t('ATOKA_DIALOG_DOWNLOAD_OPTION_ATOKA_INFO')}
							/>
						</ListItemButton>
					</ListItem>
					<ListItem disablePadding>
						<ListItemButton
							onClick={() => showUpdateCompanyDialog({ forceRefresh: false })}
							disabled={!lastImportDate}
						>
							<ListItemIcon>
								<Icon icon="server" size={32} />
							</ListItemIcon>
							<ListItemText
								primary={t('ATOKA_DIALOG_DOWNLOAD_OPTION_LOCAL')}
								secondary={
									<FormattedMessage
										id="atokaDownloadReportsDialog.lastImportDate"
										defaultMessage={t('COMPANIES_ATOKA_LAST_UPDATE')}
										values={{
											date: lastImportDate ? (
												<FormattedDate value={lastImportDate?.toDate()} />
											) : (
												'N/A'
											)
										}}
									/>
								}
							/>
						</ListItemButton>
					</ListItem>
				</List>
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
			</DialogActions>
		</>
	);
}
